import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertTriangle, ChevronDown, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../components/ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import EffectText from '../components/EffectText';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import { pickDefaultSet, splitByRelease } from '../lib/revealSets';

const NEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

const FILTERS = [
  ['all', 'setList.filters.all'],
  ['revealed', 'setList.filters.revealed'],
  ['hidden', 'setList.filters.hidden'],
];

// Frame colour → hover ring, so the grid reads by colour. Full literal class names:
// Tailwind only generates classes it can find verbatim in the source.
const COLOR_RING = {
  red: 'hover:ring-red-500/70',
  blue: 'hover:ring-sky-500/70',
  purple: 'hover:ring-violet-500/70',
  yellow: 'hover:ring-yellow-400/70',
  green: 'hover:ring-emerald-500/70',
  colorless: 'hover:ring-zinc-300/80',
};

function cardImage(card) {
  return card?.images?.revealed?.full || card?.images?.blob?.full || card?.images?.original?.full || '';
}

function enumText(v) {
  return v && typeof v === 'object' ? v.description || v.value : v;
}

function isNew(card) {
  if (card?.status !== 'revealed' || !card.revealedAt) return false;
  return Date.now() - new Date(card.revealedAt).getTime() < NEW_WINDOW_MS;
}

function formatBp(n) {
  return n == null ? null : n.toLocaleString('en-US');
}

function BattlePower({ bp }) {
  if (!bp) return null;
  const parts = [
    ['1', bp.bp1], ['2', bp.bp2], ['3', bp.bp3], ['4', bp.bp4], ['EX', bp.ex],
  ].filter(([, v]) => v != null);
  if (!parts.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {parts.map(([label, v]) => (
        <span key={label} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs tabular-nums">
          <span className="text-muted-foreground">{label === 'EX' ? 'EX' : `▮${label}`}</span>
          <span className="font-semibold">{formatBp(v)}</span>
        </span>
      ))}
    </div>
  );
}

/** Text side of a card, shown in the click dialog. */
function CardDetails({ card, t }) {
  // Scenes have a round (lit notches on the dial) instead of a level.
  const level = card.round != null
    ? t('setList.round', { round: card.round })
    : card.level != null ? `Lv.${card.level}` : null;
  return (
    <div className="flex min-w-0 flex-col gap-2 text-sm">
      <div>
        <p className="text-base font-semibold leading-tight">{card.name}</p>
        {card.typeName && <p className="text-muted-foreground leading-tight">{card.typeName}</p>}
      </div>
      <div className="flex flex-wrap gap-1">
        <Badge variant="outline" className="font-mono text-[10px]">{card.number}</Badge>
        {level && <Badge variant="secondary" className="text-[10px]">{level}</Badge>}
        {enumText(card.rarity) && <Badge variant="secondary" className="text-[10px]">{enumText(card.rarity)}</Badge>}
        {enumText(card.type) && <Badge variant="outline" className="text-[10px]">{enumText(card.type)}</Badge>}
        {enumText(card.feature) && <Badge variant="outline" className="text-[10px]">{enumText(card.feature)}</Badge>}
        {card.characterName && <Badge variant="outline" className="text-[10px]">{card.characterName}</Badge>}
      </div>
      {card.status === 'revealed' && card.verified === false && (
        <p
          className="flex w-fit items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
          title={t('setList.unverifiedHint')}
        >
          <AlertTriangle className="h-3 w-3" />
          {t('setList.unverified')}
        </p>
      )}
      {card.effect ? (
        <EffectText text={card.effect} className="leading-snug" />
      ) : card.effectRevealed === false ? (
        <p className="italic text-muted-foreground">{t('setList.effectNotRevealed')}</p>
      ) : null}
      {card.flavorText && (
        <p className="rounded-md bg-muted/40 p-2 italic text-muted-foreground">{card.flavorText}</p>
      )}
      <BattlePower bp={card.battlePower} />
    </div>
  );
}

function HiddenSlot({ number }) {
  const serial = number.split('-')[1];
  return (
    <div
      className="flex aspect-[63/88] items-center justify-center rounded-md border border-white/10 bg-gradient-to-br from-brand to-brand-accent text-white shadow-inner brightness-[.65]"
      aria-label={`${number} not yet revealed`}
    >
      <span className="text-lg font-bold tabular-nums tracking-tight sm:text-2xl">{serial}</span>
    </div>
  );
}

function CardSlot({ slot, onOpen, t }) {
  const { card } = slot;
  const landscape = card.orientation === 'landscape';
  const fresh = isNew(card);
  return (
    <HoverCard openDelay={120} closeDelay={60}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => onOpen(card)}
          className={cn(
            'group relative block aspect-[63/88] w-full overflow-hidden rounded-md bg-zinc-900 ring-1 ring-black/40 transition',
            'hover:z-10 hover:scale-[1.04] hover:ring-2 focus-visible:outline-none focus-visible:ring-2',
            COLOR_RING[card.color] || 'hover:ring-primary/60'
          )}
          aria-label={`${card.number} ${card.name}`}
        >
          <img
            src={cardImage(card)}
            alt={card.name}
            loading="lazy"
            className={cn('h-full w-full', landscape ? 'object-contain' : 'object-cover')}
          />
          {fresh && (
            <span className="absolute right-1 top-1 rounded bg-amber-400 px-1 text-[9px] font-bold uppercase text-black shadow">
              {t('setList.new')}
            </span>
          )}
        </button>
      </HoverCardTrigger>
      {/* The card image is the official source and readable at this size, so the hover is
          image-first; the transcribed text lives in the click dialog. */}
      <HoverCardContent
        side="right"
        align="center"
        collisionPadding={12}
        className={cn('p-2', landscape ? 'w-[min(92vw,600px)]' : 'w-[min(92vw,400px)]')}
      >
        <img src={cardImage(card)} alt={card.name} className="w-full rounded-md shadow-md" />
        <p className="mt-1.5 truncate px-0.5 text-xs text-muted-foreground">
          <span className="font-mono">{card.number}</span>
          {' · '}
          <span className="font-medium text-foreground">{card.name}</span>
          {card.typeName ? ` — ${card.typeName}` : ''}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

function SourceLine({ card, t }) {
  const src = card.source;
  if (card.status === 'released') {
    return <p className="text-xs text-muted-foreground">{t('setList.source.released')}</p>;
  }
  if (!src?.url) return null;
  const label = src.kind === 'x' ? t('setList.source.x') : t('setList.source.official');
  const when = src.time ? new Date(src.time).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null;
  return (
    <a href={src.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
      {label}{when ? ` · ${when}` : ''}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function useRevealSets() {
  const [sets, setSets] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/reveals')
      .then(r => (r.ok ? r.json() : { sets: [] }))
      .then(d => { if (!cancelled) setSets(d.sets || []); })
      .catch(() => { if (!cancelled) setSets([]); });
    return () => { cancelled = true; };
  }, []);
  return sets;
}

export function SetIndex() {
  const sets = useRevealSets();
  if (!sets) return <PageLoader />;
  const target = pickDefaultSet(sets);
  if (!target) return <Navigate to="/gallery" replace />;
  return <Navigate to={`/sets/${target}`} replace />;
}

const PICKER_TAB = 'flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-left text-sm transition';
const PICKER_ACTIVE = 'border-white bg-white text-slate-900 shadow';
const PICKER_IDLE = 'border-white/30 bg-white/10 text-white hover:bg-white/20';

/**
 * Switches between sets. Sets still to release get a tab each (soonest first);
 * released sets move into one "Released sets" menu, newest first, so the set being
 * revealed now is the obvious choice. Hidden with a single set.
 */
function SetPicker({ current, t }) {
  const { upcoming, released } = splitByRelease(useRevealSets());
  if (upcoming.length + released.length < 2) return null;
  const currentReleased = released.find(s => s.set === current);
  return (
    <nav aria-label={t('setList.pickerLabel')} className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1">
      {upcoming.map(s => {
        const active = s.set === current;
        return (
          <Link
            key={s.set}
            to={`/sets/${s.set}`}
            aria-current={active ? 'page' : undefined}
            className={cn(PICKER_TAB, active ? PICKER_ACTIVE : PICKER_IDLE)}
          >
            <span className="font-mono text-xs font-semibold tracking-wider">{s.set}</span>
            <span className="max-w-[12rem] truncate font-medium">{s.name}</span>
            <span className={cn('text-xs tabular-nums', active ? 'text-slate-500' : 'text-white/70')}>
              {s.revealedCount}/{s.total}
            </span>
          </Link>
        );
      })}
      {released.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(PICKER_TAB, currentReleased ? PICKER_ACTIVE : PICKER_IDLE)}
            aria-current={currentReleased ? 'page' : undefined}
          >
            {currentReleased ? (
              <>
                <span className="font-mono text-xs font-semibold tracking-wider">{currentReleased.set}</span>
                <span className="max-w-[12rem] truncate font-medium">{currentReleased.name}</span>
              </>
            ) : (
              <span className="font-medium">{t('setList.releasedSets', { count: released.length })}</span>
            )}
            <ChevronDown className="h-4 w-4 opacity-70" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {released.map(s => (
              <DropdownMenuItem key={s.set} asChild>
                <Link to={`/sets/${s.set}`} className="flex items-center gap-2">
                  <span className="font-mono text-xs font-semibold tracking-wider">{s.set}</span>
                  <span className="truncate">{s.name}</span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </nav>
  );
}

function filterSlots(slots, filter) {
  if (filter === 'revealed') return slots.filter(s => s.status !== 'hidden');
  if (filter === 'hidden') return slots.filter(s => s.status === 'hidden');
  return slots;
}

function countShown(slots) {
  return slots.filter(s => s.status !== 'hidden').length;
}

function SetGrid({ slots, filter, onOpen, t }) {
  return (
    <div className="grid grid-cols-4 gap-1.5 rounded-xl bg-slate-950 p-2 sm:grid-cols-6 sm:gap-2 sm:p-3 md:grid-cols-8 xl:grid-cols-11">
      {filterSlots(slots, filter).map(slot =>
        slot.status === 'hidden'
          ? <HiddenSlot key={slot.number} number={slot.number} />
          : <CardSlot key={slot.number} slot={slot} onOpen={onOpen} t={t} />
      )}
    </div>
  );
}

/** A companion set's grid (EXD02 on the EXB01 page), under its own small heading. */
function CompanionSection({ listing, filter, onOpen, t }) {
  const shown = countShown(listing.slots);
  return (
    <section className="mt-8" aria-labelledby={`set-${listing.set}`}>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="rounded bg-black px-2 py-0.5 font-mono text-xs tracking-[0.3em] text-white">{listing.set}</span>
        <h2 id={`set-${listing.set}`} className="text-xl font-bold tracking-tight">{listing.name}</h2>
        <span className="text-sm tabular-nums text-muted-foreground">
          {t('setList.progress', { shown, total: listing.total })}
        </span>
      </div>
      <SetGrid slots={listing.slots} filter={filter} onOpen={onOpen} t={t} />
    </section>
  );
}

function PageLoader() {
  return (
    <div className="flex justify-center py-24">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

export default function SetList() {
  const { setCode } = useParams();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setOpen(null);
    fetch(`/api/reveals/${encodeURIComponent(setCode)}`)
      .then(async r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'notFound' : 'failed');
        return r.json();
      })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [setCode]);

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center text-muted-foreground">
        {error === 'notFound' ? t('setList.notFound') : t('setList.loadFailed')}
      </div>
    );
  }
  if (!data) return <PageLoader />;
  // A companion set lives on its host's page.
  if (data.pageWith) return <Navigate to={`/sets/${data.pageWith}`} replace />;

  const shown = countShown(data.slots);
  const pct = Math.round((shown / data.total) * 100);
  const release = data.releaseDate
    ? new Date(`${data.releaseDate}T00:00:00`).toLocaleDateString(undefined, { dateStyle: 'long' })
    : null;

  return (
    <div className="min-h-full bg-background">
      <Helmet>
        <title>{`${data.set} ${data.name} — ${t('setList.title')} | Gardeno Games`}</title>
        <meta name="description" content={t('setList.metaDescription', { set: data.set, name: data.name })} />
      </Helmet>

      <header className="relative overflow-hidden bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <SetPicker current={data.set} t={t} />
          <span className="inline-block rounded bg-black px-2 py-0.5 font-mono text-xs tracking-[0.3em]">{data.set}</span>
          <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-5xl">{data.name}</h1>
          <p className="mt-1 text-sm uppercase tracking-[0.4em] text-white/80">{t('setList.title')}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <div className="min-w-[220px] flex-1 sm:max-w-sm">
              <div className="mb-1 flex justify-between">
                <span>{t('setList.progress', { shown, total: data.total })}</span>
                <span className="tabular-nums">{pct}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-black/30">
                <div className="h-full rounded-full bg-white/90" style={{ width: `${pct}%` }} />
              </div>
            </div>
            {release && (
              <span className="text-white/90">
                {t(data.releaseDate < new Date().toISOString().slice(0, 10) ? 'setList.released' : 'setList.releases',
                  { date: release })}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition',
                filter === key ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'
              )}
              aria-pressed={filter === key}
            >
              {t(label)}
            </button>
          ))}
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" />
            {t('setList.hint')}
          </span>
        </div>

        <SetGrid slots={data.slots} filter={filter} onOpen={setOpen} t={t} />
        {(data.companions || []).map(c => (
          <CompanionSection key={c.set} listing={c} filter={filter} onOpen={setOpen} t={t} />
        ))}

        <p className="mt-4 text-xs text-muted-foreground">{t('setList.disclaimer')}</p>
      </div>

      <Dialog open={!!open} onOpenChange={o => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle>{open.name}</DialogTitle>
                <DialogDescription asChild>
                  <div><SourceLine card={open} t={t} /></div>
                </DialogDescription>
              </DialogHeader>
              <div className={cn('flex flex-col gap-5', open.orientation !== 'landscape' && 'sm:flex-row')}>
                <img
                  src={cardImage(open)}
                  alt={open.name}
                  className={cn('rounded-lg shadow-lg', open.orientation === 'landscape' ? 'w-full' : 'mx-auto w-72 flex-shrink-0 self-start')}
                />
                <CardDetails card={open} t={t} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
